import { VIDALYTICS_PLAYER_ID, VIDALYTICS_CONVERSIONS } from '~/data/masterclass/landing-content';

// Client-side Vidalytics conversion tracking. Fires the lead/sale conversion so
// Vidalytics attributes it to the VSL the viewer watched (in the same browser
// session that has the Vidalytics viewer cookie). This is Vidalytics-proprietary
// attribution — it goes to stats.vidalytics.com, NOT our sGTM; player engagement
// events are the ones we route through our own pipeline. Best-effort: a tracking
// failure must never block the funnel.
type VidalyticsConversionTracking = {
  track: (trackingId: string, cfg: { conversionId: string; value?: number }) => void;
};
type VidalyticsConversionWindow = Window & {
  VidalyticsC?: { ConversionTracking?: new (opts: { url: string }) => VidalyticsConversionTracking };
};

export const useVidalyticsConversion = () => {
  const track = (conversionId: string, value?: number) => {
    if (typeof window === 'undefined' || !conversionId) return;
    try {
      const w = window as VidalyticsConversionWindow;
      const run = () => {
        try {
          const CT = w.VidalyticsC?.ConversionTracking;
          if (!CT) return;
          const cfg: { conversionId: string; value?: number } = { conversionId };
          if (typeof value === 'number' && Number.isFinite(value)) cfg.value = value;
          new CT({ url: 'https://stats.vidalytics.com' }).track(VIDALYTICS_PLAYER_ID, cfg);
        } catch {
          // best-effort
        }
      };
      // Each conversion loads conversion.min.js with its own ?cid=<conversionId>
      // (mirrors the dashboard snippet), then instantiates + fires.
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `https://fast.vidalytics.com/js/conversion.min.js?cid=${conversionId}`;
      script.onload = run;
      document.getElementsByTagName('head')[0].appendChild(script);
    } catch {
      // best-effort — never throw into the funnel
    }
  };

  const trackLead = () => track(VIDALYTICS_CONVERSIONS.lead);
  const trackSale = (value?: number) => track(VIDALYTICS_CONVERSIONS.sale, value);

  return { track, trackLead, trackSale };
};
